import React, { useEffect, useState, useCallback } from 'react';
import {
    Grid,
    Typography,
    Box,
    Button,
    IconButton,
    CardHeader,
    CardContent,
    CardActions,
    MenuItem,
    List,
    ListItem,
    ListItemText,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Stack,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Loader, FormCard, Menu, Accordion } from "../../../../../../Components";
import { setModal } from "../../../../../Common/App";
import {
    HolidayModal,
    DeleteHolidayModal,
    fetchHolidays,
} from '../../../index';
import {usePermissions} from "../../../../../../Hooks/usePermissions";
import dayjs from 'dayjs';

export const HolidayTab = () => {

    const dispatch = useDispatch();
    const holidaysDirty = useSelector((store) => store.settings?.holidays?.is_dirty);
    const [isLoading, setIsLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [menuState, setMenuState] = useState({});
    const {hasPermission, hasAnyPermission} = usePermissions();
    const currentYear = dayjs().year();

    // Group consecutive holidays with same name
    const groupConsecutiveHolidays = useCallback((holidays) => {
        if (!holidays || holidays.length === 0) return [];

        const sorted = [...holidays].sort((a, b) => {
            const dateA = dayjs(a.holiday_date);
            const dateB = dayjs(b.holiday_date);
            if (dateA.isSame(dateB)) {
                return a.holiday_name.localeCompare(b.holiday_name);
            }
            return dateA.diff(dateB);
        });

        const groups = [];
        let currentGroup = null;

        sorted.forEach((holiday) => {
            const holidayDate = dayjs(holiday.holiday_date);

            const isSameName = currentGroup && currentGroup.name === holiday.holiday_name;
            const isSameDate = currentGroup && holidayDate.isSame(dayjs(currentGroup.endDate), 'day');
            const isConsecutive = currentGroup && holidayDate.isSame(dayjs(currentGroup.endDate).add(1, 'day'), 'day');

            if (!currentGroup ||
                !isSameName ||
                (!isSameDate && !isConsecutive)) {
                // Start new group
                currentGroup = {
                    id: holiday.id,
                    name: holiday.holiday_name,
                    startDate: holiday.holiday_date,
                    endDate: holiday.holiday_date,
                    items: [holiday],
                    description: holiday.description,
                };
                groups.push(currentGroup);
            } else {
                // Add to existing group (same date or consecutive date)
                if (!isSameDate) {
                    currentGroup.endDate = holiday.holiday_date;
                }
                currentGroup.items.push(holiday);
            }
        });

        return groups;
    }, []);

    // Fetch data - only current year holidays
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        await fetchHolidays({ dispatch, filters: { paginate: false, sort_by: 'holiday_date', sort_dir: 'asc', columns: ['id', 'holiday_name', 'holiday_date', 'description']} }).then((res) => {
            if (res.success) {
                const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                // Filter to only current year holidays
                const currentYearHolidays = data.filter(item => {
                    if (!item.holiday_date) return false;
                    return dayjs(item.holiday_date).year() === currentYear;
                });
                const grouped = groupConsecutiveHolidays(currentYearHolidays);
                setItems(grouped);
            }
        });
        setIsLoading(false);
    }, [dispatch, currentYear, groupConsecutiveHolidays]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Auto-refresh when holidays are modified
    useEffect(() => {
        if (holidaysDirty) {
            fetchData();
        }
    }, [holidaysDirty, fetchData]);

    // Handle menu open/close
    const handleClick = (event, id) => {
        event.stopPropagation();
        setMenuState((prev) => ({
            ...prev,
            [id]: event.currentTarget,
        }));
    };

    const handleClose = (id) => {
        setMenuState((prev) => ({
            ...prev,
            [id]: null,
        }));
    };

    // Handle delete
    const handleDelete = (e, group) => {
        e.stopPropagation();
        // Pass group to delete modal
        dispatch(setModal({component: <DeleteHolidayModal group={group} />, open: true,}));
    };

    // Handle edit
    const handleEdit = (e, group) => {
        e.stopPropagation();
        // Create edit item with start_date and end_date from group
        const editItem = {
            id: group.items[0].id,
            holiday_name: group.name,
            holiday_date: group.startDate,
            start_date: group.startDate,
            end_date: group.endDate,
            description: group.description,
        };
        dispatch(setModal({component: <HolidayModal item={editItem} />,open: true,}));
    };

    if (!hasPermission('core.settings.holiday.menu')) {
        return (
            <FormCard>
                <CardHeader title="Holiday management" />
                <CardContent>
                    <Typography variant="body2" color="text.secondary">
                        You do not have access to holiday management. Ask an administrator to grant the{' '}
                        <strong>core.settings.holiday.menu</strong> permission.
                    </Typography>
                </CardContent>
            </FormCard>
        );
    }

    return (
        <FormCard>
            <CardHeader
                title={`Holiday Management - ${currentYear}`}
                avatar={<Loader isLoading={isLoading} type={"linear"} />}
                action={hasPermission('core.settings.holiday.add') ? (
                    <Button
                        variant="contained"
                        size="small"
                        color="info"
                        onClick={() => dispatch(setModal({component: <HolidayModal />, open: true,}))}
                    >
                        + New Holiday
                    </Button>
                ) : null}
            />
            <CardContent sx={{ pb: '1rem!important' }}>
                <Grid container spacing={2}>
                    {items.length > 0 ? (
                        <Grid item xs={12}>
                            {items.map((group) => {
                                const startDate = dayjs(group.startDate);
                                const endDate = dayjs(group.endDate);
                                const isSingleDay = startDate.isSame(endDate, 'day');
                                const dateLabel = isSingleDay 
                                    ? startDate.format('MMM D, YYYY')
                                    : `${startDate.format('MMM D')} - ${endDate.format('MMM D, YYYY')}`;
                                const groupKey = `${group.name}-${group.startDate}-${group.endDate}`;

                                return (
                                    <Accordion key={groupKey} rounded={0} elevation={0}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls={`panel-${groupKey}-content`}
                                            id={`panel-${groupKey}-header`}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                flexDirection: 'row-reverse',
                                            }}
                                        >
                                            {/* Title and Expand Icon */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                                <Typography sx={{ flexGrow: 1 }}>{group.name}</Typography>
                                                <Stack direction={"row"} justifyContent={"space-between"} spacing={1}>
                                                    <Chip variant={"outlined"} color={"primary"} size="small" label={dateLabel} />
                                                </Stack>
                                            </Box>

                                            {/* Action Buttons */}
                                            {hasAnyPermission(['core.settings.holiday.edit','core.settings.holiday.delete']) && (
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <IconButton
                                                        aria-label="more"
                                                        id={`menu-button-${groupKey}`}
                                                        aria-controls={menuState[groupKey] ? `menu-${groupKey}` : undefined}
                                                        aria-expanded={menuState[groupKey] ? 'true' : undefined}
                                                        aria-haspopup="true"
                                                        onClick={(e) => handleClick(e, groupKey)}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                    <Menu
                                                        id={`menu-${groupKey}`}
                                                        anchorEl={menuState[groupKey]}
                                                        open={Boolean(menuState[groupKey])}
                                                        onClose={() => handleClose(groupKey)}
                                                        anchorOrigin={{
                                                            vertical: 'bottom',
                                                            horizontal: 'right',
                                                        }}
                                                        transformOrigin={{
                                                            vertical: 'top',
                                                            horizontal: 'right',
                                                        }}
                                                    >
                                                        {hasPermission('core.settings.holiday.edit') && (
                                                            <MenuItem onClick={(e) => handleEdit(e, group)}>
                                                                <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
                                                            </MenuItem>
                                                        )}
                                                        {hasPermission('core.settings.holiday.delete') && (
                                                            <MenuItem onClick={(e) => handleDelete(e, group)}>
                                                                <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
                                                            </MenuItem>
                                                        )}
                                                    </Menu>
                                                </Box>
                                            )}
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <List>
                                                <ListItem>
                                                    <ListItemText 
                                                        primary="Date Range" 
                                                        secondary={isSingleDay 
                                                            ? startDate.format('MMMM D, YYYY')
                                                            : `${startDate.format('MMMM D, YYYY')} - ${endDate.format('MMMM D, YYYY')}`
                                                        } 
                                                    />
                                                </ListItem>
                                                {!isSingleDay && (
                                                    <ListItem>
                                                        <ListItemText 
                                                            primary="Duration" 
                                                            secondary={`${group.items.length} day(s)`} 
                                                        />
                                                    </ListItem>
                                                )}
                                                {group.description && (
                                                    <ListItem>
                                                        <ListItemText primary="Description" secondary={group.description} />
                                                    </ListItem>
                                                )}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                        </Grid>
                    ) : (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                                No holidays found for {currentYear}. Add your first holiday to get started.
                            </Typography>
                            
                        </Grid>
                    )}
                </Grid>
            </CardContent>
            {hasPermission('core.settings.holiday.add') && (
                <CardActions>
                    <Typography variant="body2" color="text.secondary">
                        Note: Changes to the holiday calendar are global. Please log out and log back in for these settings to take effect across the system.
                    </Typography>
                </CardActions>
            )}
        </FormCard>
    );
};

